'use client'

import { Cell, Spacer, Text } from '@vinted/web-ui'
import { useEffect, useRef } from 'react'

import { toggleOneTrustInfoDisplay } from '@marketplace-web/shared/consent'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { clickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { VanAdvertiserDto } from '@marketplace-web/domain/ads-data'

type Props = {
  vanCampaign: VanAdvertiserDto
}

const VanPrivacyPolicy = ({ vanCampaign }: Props) => {
  const translate = useTranslate('vinted_ads_network.privacy_policy')
  const ref = useRef<HTMLDivElement>(null)
  const { track } = useTracking()
  const { isWebview } = useSession()

  const { advertiser_name, paid_by } = vanCampaign

  const isIdentical = advertiser_name === paid_by

  useEffect(() => {
    const element = ref.current
    if (!element) {
      return undefined
    }

    const anchor = element.querySelector('a')
    if (!anchor) {
      return undefined
    }

    const handleClick = (event: MouseEvent) => {
      track(
        clickEvent({
          target: ClickableElement.CookieSettings,
          screen: Screen.VanPrivacyPolicy,
        }),
      )

      if (isWebview) {
        return
      }

      event.preventDefault()
      toggleOneTrustInfoDisplay()
    }

    anchor.addEventListener('click', handleClick)

    return () => {
      anchor.removeEventListener('click', handleClick)
    }
  }, [track, isWebview])

  return (
    <Cell styling={Cell.Styling.Wide}>
      <div className="van-privacy-policy">
        <Text type={Text.Type.Heading} as="h1" text={translate('title')} />
        <Spacer size={Spacer.Size.Medium} />
        <Text
          type={Text.Type.Body}
          as="p"
          text={
            isIdentical
              ? translate('advertiser_name', { advertiser_name })
              : translate('advertiser_and_payer_names', {
                  advertiser_name,
                  paid_by,
                })
          }
          format
          html
        />
        <Spacer size={Spacer.Size.Medium} />
        <Text type={Text.Type.Body} as="div" text={translate('content')} html />
        <Spacer size={Spacer.Size.Medium} />
        <div ref={ref}>
          <Text type={Text.Type.Body} as="p" text={translate('settings')} html />
        </div>
      </div>
    </Cell>
  )
}

export default VanPrivacyPolicy
